// cSpell:ignore afrekeningen bevestigde Bevestigen Gebruikt Gelijke lopende nieuwe sleutel verdeelsleutel Verdeelsleutels verdeling
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatNumber } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  Banner,
  Card,
  DataTable,
  DropdownMenu,
  EmptyState,
  Icon,
  Loading,
  Modal,
  Stack,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { keyUsage } from "../costsList.utils";
import { useCanEditCosts, useKeys, useSettlements, useUnits } from "../hooks";
import { keyDisplayName } from "../keyName.utils";
import {
  acceptedNumber,
  formatNumberInput,
  numberInputErrorText,
  parseLocaleNumber,
} from "../numberInput.utils";
import { ApportionmentKey } from "../types";

const RowItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  &:last-child {
    border-bottom: 0;
  }
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: ${FontWeights.Bold};
  color: ${({ theme }) => theme.getColor("gray", 1000)};
`;

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.getColor("gray", 600)};
  margin-top: 2px;
`;

const Kebab = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

// Total row under the unit inputs — mirrors the totals styling of the cost
// tables (label left, tabular number right).
const BasisRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 12px 8px 0;
  border-top: 2px solid ${({ theme }) => theme.getColor("gray", 200)};
  font-weight: ${FontWeights.Bold};
  .value {
    font-variant-numeric: tabular-nums;
  }
`;

export interface IApportionmentKeysTabProps {
  groupId: string;
}

export const ApportionmentKeysTab: React.FC<IApportionmentKeysTabProps> = ({
  groupId,
}) => {
  const { items: keys, isFetching, create, update, remove } = useKeys(groupId);
  const canEdit = useCanEditCosts(groupId);
  const { items: units } = useUnits(groupId);
  const [editing, setEditing] = useState<ApportionmentKey | null>(null);
  const [creating, setCreating] = useState(false);
  // The group's settlements, to say where a key is used before it changes.
  const { items: settlements } = useSettlements({ groupId });

  // "Gebruikt in 1 bevestigde en 2 lopende afrekeningen ..." — undefined for
  // a key no settlement uses.
  const usageText = (
    key: ApportionmentKey,
    textKey: "costs.keys.usage_edit" | "costs.keys.usage_delete",
  ): string | undefined => {
    const { confirmed, drafts } = keyUsage({ keyId: key.id, settlements });
    if (confirmed + drafts === 0) return undefined;
    return getLocalizedText(textKey, {
      confirmed: String(confirmed),
      drafts: String(drafts),
    });
  };

  // Only user-created keys are listed/managed here; the system "Gelijke verdeling"
  // key stays available in the settlement distribution dropdown.
  const userKeys = keys.filter(k => !k.isSystem);

  // Only the group's current units count: a weight left behind by a unit
  // that is no longer in the group plays no part in any split.
  const currentWeights = (k: ApportionmentKey): number[] =>
    units.length > 0
      ? units.map(u => k.weights?.[u.id] ?? 0)
      : Object.values(k.weights ?? {});
  const basisOf = (k: ApportionmentKey) =>
    currentWeights(k).reduce((s, v) => s + (v || 0), 0);

  const handleDelete = (key: ApportionmentKey) => {
    const usage = usageText(key, "costs.keys.usage_delete");
    confirm({
      title: getLocalizedText("costs.keys.delete"),
      info: [
        getLocalizedText("costs.keys.delete_confirm", {
          name: keyDisplayName(key.name),
        }),
        usage,
      ]
        .filter(Boolean)
        .join(" "),
      primaryActions: [
        {
          title: getLocalizedText("costs.action.delete"),
          onPress: () => void remove(key.id),
        },
      ],
    });
  };

  if (isFetching) {
    return (
      <Card heading={getLocalizedText("costs.keys.title")}>
        <Loading />
      </Card>
    );
  }

  return (
    <Card
      heading={getLocalizedText("costs.keys.title")}
      primaryActions={
        canEdit
          ? [
              {
                content: getLocalizedText("costs.keys.new"),
                onClick: () => setCreating(true),
              },
            ]
          : []
      }
    >
      {userKeys.length === 0 ? (
        <EmptyState
          heading={getLocalizedText("costs.keys.empty")}
          icon="accountingDocument"
        >
          {getLocalizedText("costs.keys.empty_body")}
        </EmptyState>
      ) : (
        <div>
          {userKeys.map(k => (
            <RowItem key={k.id}>
              <Main>
                <Name>{keyDisplayName(k.name)}</Name>
                <Sub>
                  {getLocalizedText("costs.keys.summary", {
                    base: formatNumber(basisOf(k), false),
                    count: String(currentWeights(k).filter(w => w > 0).length),
                  })}
                </Sub>
              </Main>
              {canEdit && (
                <DropdownMenu
                  actions={[
                    {
                      content: getLocalizedText("costs.keys.change"),
                      onClick: () => setEditing(k),
                    },
                    {
                      content: getLocalizedText("costs.action.delete"),
                      onClick: () => handleDelete(k),
                    },
                  ]}
                >
                  <Kebab>
                    <Icon source="navigationMenuVertical" />
                  </Kebab>
                </DropdownMenu>
              )}
            </RowItem>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <KeyModal
          units={units}
          initial={editing ?? undefined}
          usageWarning={
            editing ? usageText(editing, "costs.keys.usage_edit") : undefined
          }
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSave={async payload => {
            if (editing) await update(editing.id, payload);
            else
              await create({
                groupId,
                isEqual: false,
                name: payload.name ?? getLocalizedText("costs.keys.new"),
                weights: payload.weights ?? {},
              });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
};

interface KeyModalProps {
  units: { id: string; code: string; address?: string }[];
  initial?: ApportionmentKey;
  // Where the key being edited is used, shown above the form.
  usageWarning?: string;
  onClose: () => void;
  onSave: (payload: Partial<ApportionmentKey>) => Promise<void> | void;
}

// Read the Belgian way, like every number field of the feature: "2.450" is a
// share of 2450, "80,5" one of 80,5. Unreadable text counts as no share.
const parseWeight = (raw: string): number => acceptedNumber(raw) ?? 0;

const weightErrorFor = (raw?: string): string | undefined =>
  numberInputErrorText({ error: parseLocaleNumber(raw ?? "").error });

// The key name column holds 100 characters (rentio-api-next refuses more).
const KEY_NAME_MAX_LENGTH = 100;

// Exported so the cost wizard can offer "nieuwe verdeelsleutel" inline: a
// building cost that is settled straight away has to pick a sleutel, and
// sending the user off to the Verdeelsleutels-tab would throw away the cost
// they are halfway through entering.
export const KeyModal: React.FC<KeyModalProps> = ({
  units,
  initial,
  usageWarning,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [weightsError, setWeightsError] = useState<string | undefined>(
    undefined,
  );
  // Inputs live as strings so a unit without a share shows an EMPTY field
  // (placeholder 0) instead of a pre-filled 0 the user must clear first.
  const [weightsStr, setWeightsStr] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [unitId, w] of Object.entries(initial?.weights ?? {})) {
      if (w) out[unitId] = formatNumberInput(w);
    }
    return out;
  });

  // Saved for the group's current units only, so a stale weight of a unit
  // that left the group drops out instead of surviving every edit.
  const parsedWeights = (): Record<string, number> => {
    const unitIds = new Set(units.map(u => u.id));
    const out: Record<string, number> = {};
    for (const [unitId, raw] of Object.entries(weightsStr)) {
      const n = parseWeight(raw);
      if (n > 0 && (unitIds.size === 0 || unitIds.has(unitId))) {
        out[unitId] = n;
      }
    }
    return out;
  };
  const basis = Object.values(parsedWeights()).reduce((s, v) => s + v, 0);
  // "Bevestigen" saves once: a double click created the key twice. The ref
  // stops a click that lands before the disabled button renders. Only a
  // failure releases it: a successful save closes the modal.
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const handle = async () => {
    if (isSavingRef.current) return;
    if (!name.trim()) {
      setNameError(getLocalizedText("costs.keys.name_required"));
      return;
    }
    // A share the field could not read must not silently drop out of the
    // key; the field already says what is wrong.
    if (units.some(u => weightErrorFor(weightsStr[u.id]))) return;
    // A key without any weight would silently push 100% of every cost to the
    // owner (the engine treats a zero denominator as "structural owner").
    if (basis <= 0) {
      setWeightsError(getLocalizedText("costs.keys.needs_share"));
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      await onSave({ name: name.trim(), weights: parsedWeights() });
    } catch (error) {
      isSavingRef.current = false;
      setIsSaving(false);
      throw error;
    }
  };

  return (
    <Modal
      heading={getLocalizedText(initial ? "costs.keys.edit" : "costs.keys.new")}
      onClose={onClose}
      actions={[
        {
          content: getLocalizedText("costs.action.cancel"),
          appearance: "outline",
          onClick: onClose,
        },
        {
          content: getLocalizedText("costs.action.confirm"),
          appearance: "primary",
          isSubmitting: isSaving,
          onClick: handle,
        },
      ]}
    >
      <Stack vertical spacing="loose">
        {usageWarning && (
          <Banner variation="warning" hasDismiss={false}>
            {usageWarning}
          </Banner>
        )}
        <TextField
          name="key-name"
          label={getLocalizedText("costs.keys.name_label")}
          placeholder={getLocalizedText("costs.keys.name_placeholder")}
          value={name}
          maxLength={KEY_NAME_MAX_LENGTH}
          error={nameError}
          onChange={(e: any) => {
            setName(e.target.value);
            if (nameError) setNameError(undefined);
          }}
        />
        <TextStyle variation="subdued">
          {getLocalizedText("costs.keys.shares_intro")}
        </TextStyle>
        <DataTable
          columnContentTypes={["text", "numeric"]}
          headings={[
            getLocalizedText("costs.field.property"),
            getLocalizedText("costs.keys.share"),
          ]}
          rows={units.map(u => ({
            id: u.id,
            content: [
              u.address ?? u.code,
              <div style={{ maxWidth: 140, marginLeft: "auto" }}>
                <TextField
                  name={`weight-${u.id}`}
                  label="_"
                  labelHidden
                  type="text"
                  inputMode="decimal"
                  placeholder={getLocalizedText("costs.keys.share")}
                  value={weightsStr[u.id] ?? ""}
                  error={weightErrorFor(weightsStr[u.id])}
                  onChange={(e: any) =>
                    setWeightsStr({
                      ...weightsStr,
                      [u.id]: e.target.value,
                    })
                  }
                  // Show what was understood ("2.450" stays a share of 2450).
                  onBlur={() => {
                    const weight = acceptedNumber(weightsStr[u.id] ?? "");
                    if (weight == null) return;
                    setWeightsStr({
                      ...weightsStr,
                      [u.id]: formatNumberInput(weight),
                    });
                  }}
                />
              </div>,
            ],
          }))}
        />
        <BasisRow>
          <span>{getLocalizedText("costs.keys.base")}</span>
          <span className="value">{formatNumber(basis, false)}</span>
        </BasisRow>
        {weightsError && basis <= 0 && (
          <Banner
            title={getLocalizedText("costs.keys.incomplete_title")}
            variation="warning"
            hasDismiss={false}
          >
            {weightsError}
          </Banner>
        )}
      </Stack>
    </Modal>
  );
};
